<template>
	<v-container class="d-flex pa-0 pb-10">
		<v-navigation-drawer
			v-model="drawer"
			:mini-variant.sync="mini"
			permanent
			width="250"
			class="report-sidebar"
		>
			<v-list-item class="px-2" @click.stop="mini = !mini">
				<v-list-item-title class="title" v-if="!mini">
					{{ txt.reportListTitle }}
				</v-list-item-title>
				<v-btn
					icon
					@click.stop="mini = !mini"
				>
				<v-icon>{{ mini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
				</v-btn>
			</v-list-item>

			<v-divider></v-divider>

			<v-list>
				<v-list-item
					v-for="report in reports"
					:key="report"
					@click="selectedReports = report"
					:class="{
						'custom-active-item': selectedReports === report && !mini,
						'margin-transition': true,
						'mx-2 my-0': !mini
					}"
					class="rounded"
				>
				<v-list-item-icon class="mr-2 ml-0 pl-0">
					<v-icon>mdi-file-chart</v-icon>
				</v-list-item-icon>
				<v-list-item-content>
					<v-list-item-title>{{ report }}</v-list-item-title>
				</v-list-item-content>
				</v-list-item>
			</v-list>
		</v-navigation-drawer>

		<v-main class="flex-grow-1">
			<div v-if="loading">{{ txt.loading }}</div>
			<progress-bar :isLoading="loading" />
			<v-alert type="error" v-show="error">
				{{ txt.error }}
			</v-alert>

			<v-card v-if="isRlsPending" class="ma-4 pa-2" max-width="480">
				<v-card-text>{{ txt.rlsPrompt }}</v-card-text>
				<v-card-text class="pt-0">
					<v-autocomplete
						v-model="rlsInputValue"
						:items="currentInstallations"
						item-text="text"
						item-value="value"
						:label="txt.rlsLabel"
						outlined
						dense
						hide-details
					/>
				</v-card-text>
				<v-card-actions>
					<v-btn
						color="primary"
						:loading="rlsLoading"
						:disabled="!rlsInputValue"
						@click="loadReportWithRls"
					>
						{{ txt.rlsButton }}
					</v-btn>
				</v-card-actions>
			</v-card>

			<div v-else class="report-container">
				<section id="container"/>
			</div>

			<!-- snackbar about the loaded reports -->
			<v-snackbar
				class="pbi-snackbar"
				v-model="showMessage"
				:color="snackbarColor"
				outlined
				:timeout="timeout"
			>
					{{ txt.loaded }}

				<template v-slot:action="{ attrs }">
					<v-icon
						color="success"
						v-bind="attrs"
						@click="showMessage = false"
					>
						mdi-close
					</v-icon>
				</template>
			</v-snackbar>
		</v-main>
	</v-container>
</template>

<script>
import ProgressBar from '../vueBasics/Progressbar.vue'
import AutocompleteBasicWithEmits from '../vueBasics/AutocompleteBasicWithEmits.vue'
import * as pbi from 'powerbi-client'
import axios from 'axios'

export default {
	name: 'PowerBI',
	props: {
		msg: String,
	},
	components: {
		ProgressBar,
		AutocompleteBasicWithEmits,
	},
	data() {
		return {
			txt: {
				loading: 'Your data is being loaded, please wait...',
				error: 'An error has occurred while loading your data',
				loaded: 'Your reports have successfully loaded',
				close: 'Close',
				reportsList: 'Select a report to display',
				reportListTitle: 'Power BI Reports',
				rlsPrompt: 'Enter a filter value to load this report',
				rlsLabel: 'Installation',
				rlsButton: 'Load Report',
			},
			loading: false,
			error: false,
			loaded: false,
			reports: [],
			selectedReports: [],
			tokenResponse: [],
			rlsRequiredReports: [],
			rlsLoadedReports: [],
			rlsInputValue: null,
			rlsLoading: false,
			installations: [],
			embedConfig: {
				type: 'report',
				tokenType: pbi.models.TokenType.Embed,
				permissions: pbi.models.Permissions.All,
				accessToken: null,
				embedUrl: null,
				id: null,
				settings: {
					filterPaneEnabled: false,
					navContentPaneEnabled: true,
					background: pbi.models.BackgroundType.Default,
				},
			},
			showMessage: false,
			snackbarColor: 'success',
			drawer: true,
			mini: false,
		}
	},
	methods: {
		updateModel(value) {
			this.selectedReports = [value]
		},
		async getToken() {
			try {
				this.error = false
				this.loading = true
				const r = await axios.get(`${config.baseAPIurl}/powerbi/get-embed-token`)
				const pending = r.data.filter((el) => el.needsRlsInput)
				const normal = r.data.filter((el) => !el.needsRlsInput)
				this.rlsRequiredReports = pending.map((el) => ({
					reportName: el.reportName,
					reportAzureId: el.reportAzureId,
				}))
				this.tokenResponse = normal
				this.reports = [
					...normal.map((el) => el.embedUrl[0].reportName),
					...pending.map((el) => el.reportName),
				]
				this.loading = false
				this.loaded = true
			} catch (error) {
				this.error = true
			} finally {
				this.loading = false
				this.displayMessage()
			}
		},

		async setUpReport() {
			const reportContainer = document.getElementById('container')
			powerbi.bootstrap(reportContainer, { type: 'report' })
			const report = powerbi.embed(reportContainer, this.embedConfig)
			report.off('loaded')
			// Triggers when a report schema is successfully loaded
			report.on('loaded', async () => {
				console.log('Report load successful')
				// await this.setFiters(report)
			})

			// Clear any other rendered handler events
			report.off('rendered')

			// Triggers when a report is successfully embedded in UI
			report.on('rendered', () => {
				console.log('Report render successful')
				report.getPages().then(filters => {
					console.log(filters)
				})
			})

			// Clear any other error handler events
			report.off('error')

			// Handle embed errors
			report.on('error', (event) => {
				const errorMsg = event.detail
				console.error(errorMsg)
				return
			})
		},
		async loadReportWithRls() {
			const pending = this.rlsRequiredReports.find((r) => r.reportName === this.selectedReports)
			if (!pending) return
			try {
				this.rlsLoading = true
				this.error = false
				const r = await axios.get(`${config.baseAPIurl}/powerbi/get-embed-token-with-rls`, {
					params: { reportAzureId: pending.reportAzureId, rlsValue: this.rlsInputValue },
				})
				this.tokenResponse = [...this.tokenResponse, r.data]
				this.embedConfig.accessToken = `${r.data.accessToken}`
				this.embedConfig.embedUrl = r.data.embedUrl[0].embedUrl
				this.embedConfig.id = r.data.embedUrl[0].reportId
				this.rlsLoadedReports = [...this.rlsLoadedReports, pending.reportName]
				await this.$nextTick()
				await this.setUpReport()
			} catch (error) {
				this.error = true
			} finally {
				this.rlsLoading = false
			}
		},
		async fetchInstallations() {
			try {
				const r = await axios.get(`${config.baseAPIurl}/powerbi/installations`)
				this.installations = r.data
			} catch (error) {
				this.error = true
			}
		},
		displayMessage() {
			this.showMessage = true
			setTimeout(() => {
				this.showMessage = false
			}, 5000)
		},
	},
	computed: {
		isRlsPending() {
			return (
				this.rlsRequiredReports.some((r) => r.reportName === this.selectedReports) &&
				!this.rlsLoadedReports.includes(this.selectedReports)
			)
		},
		currentInstallations() {
			return this.installations
		},
	},
	async created() {
		await Promise.all([this.getToken(), this.fetchInstallations()])
	},
	watch: {
		selectedReports: {
			async handler(value) {
				if (this.rlsRequiredReports.some((r) => r.reportName === value)) {
					this.rlsInputValue = null
					this.rlsLoadedReports = this.rlsLoadedReports.filter((name) => name !== value)
					return
				}
				const reportContainer = document.getElementById('container')
				const selectedReport = value
				const config = this.tokenResponse.filter(el => el.embedUrl[0].reportName === selectedReport)[0]
				powerbi.reset(reportContainer)
				if (!config) return
				this.embedConfig.accessToken = `${config.accessToken}`
				this.embedConfig.embedUrl = config.embedUrl[0].embedUrl
				this.embedConfig.id = config.embedUrl[0].reportId
				await this.setUpReport()
			},
		},
	},
}
</script>

<style lang="scss" scoped>
@import '../../../src/assets/scss/_styling.scss';
.report-sidebar {
	height: calc(100vh - 64px - 60px);
	margin-bottom: 30px;
}

.report-container {
	position: relative;
	width: 100%;
	padding-bottom: 56.25%;
	height: calc(100vh - 64px);
}

.report-container section {
	position: absolute;
	top: 20;
	left: 0;
	width: 100%;
	height: 100%;
}

.autocomplete-reports-list {
	padding: 15px 0 0 0;
	margin: 0;
}

.container {
	max-width: 100%;
	height: 100%;
}

.pbi-snackbar {
	position: absolute;
	bottom: 42px;
}

.v-navigation-drawer {
  transition: 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-active-item {
  background-color: $alturos-blue !important;
  color: white !important;
}

.v-list-item {
  border-radius: 4px;
}

.v-list-item::before {
  border-radius: 4px;
}

.margin-transition {
	transition: margin 0.3s ease;
}

</style>
